/**
 * SHARED RELATION MICROGRAMMARS — one reviewed sentence pattern per plan
 * relation, reused across shapes and domains (narration review 2026-09-26,
 * Option D: no shape × domain × audience template cross-product). A grammar
 * places governed labels and binding placeholders; it never writes a digit, and
 * a relation or state it has no reviewed pattern for yields `null`, never a guess.
 */
import type { AskStorySegment } from '../contracts/declared-storyline';
import type { AskPlanFact } from './narrative-plan';

type Grammar = (fact: AskPlanFact) => readonly AskStorySegment[] | null;

const text = (value: string): AskStorySegment => ({ text: value });
const figure = (placeholder: string): AskStorySegment => ({ figure: placeholder });

/** " for {scope}" unless the scope is the subject itself (no "X for X"). */
function scopeClause(fact: AskPlanFact): string {
  const scope = fact.labels.scope;
  return scope && scope !== fact.subject ? ` for ${scope}` : '';
}

/**
 * Answer-first change (CPO review 2026-09-26), citing the governing fact's two
 * figures — the change and its endpoint:
 *   up:   "{subject} for {scope} rose {change} to {to} in {period} vs {prior}."
 *   down: "{subject} for {scope} fell to {to} in {period} vs {prior}, a change of {change}."
 * The engine publishes a signed change, never an absolute one, so a decrease
 * states the endpoint first rather than print "fell -$X". A percent appears only
 * when the engine publishes it as a finding (none on this path yet).
 */
const change: Grammar = (fact) => {
  const { fromPeriod, toPeriod } = fact.labels;
  const { change: delta, to } = fact.figures;
  if (!delta || !to || !fromPeriod || !toPeriod) return null;
  const subject = `${fact.subject}${scopeClause(fact)}`;
  const when = ` in ${toPeriod} vs ${fromPeriod}`;
  if (fact.states.includes('increase')) {
    return [text(`${subject} rose `), figure(delta), text(' to '), figure(to), text(`${when}.`)];
  }
  if (fact.states.includes('decrease')) {
    return [
      text(`${subject} fell to `),
      figure(to),
      text(`${when}, a change of `),
      figure(delta),
      text('.'),
    ];
  }
  return null;
};

/** "{part} of {whole} {subject} is at risk." — the at-risk headline. */
const atRisk: Grammar = (fact) => {
  if (!fact.figures.part || !fact.figures.whole) return null;
  return [
    figure(fact.figures.part),
    text(' of '),
    figure(fact.figures.whole),
    text(` ${fact.subject} is at risk.`),
  ];
};

/**
 * The value-capture headline (helix-f9):
 *   "{realized} of {whole} {subject} is realized as of {asOf};
 *    {validated} is validated by approved decisions."
 * An untracked stage drops its clause; with no actuals the validated stage leads.
 */
const valueCapture: Grammar = (fact) => {
  const { whole, realized, validated } = fact.figures;
  const approved = 'validated by approved decisions';
  if (!whole) return null;
  if (realized && fact.labels.asOf) {
    const head = `${fact.subject} is realized as of ${fact.labels.asOf}`;
    const opening = [figure(realized), text(' of '), figure(whole)];
    return validated
      ? [...opening, text(` ${head}; `), figure(validated), text(` is ${approved}.`)]
      : [...opening, text(` ${head}.`)];
  }
  if (!validated) return null;
  return [figure(validated), text(' of '), figure(whole), text(` ${fact.subject} is ${approved}.`)];
};

const partOfWhole: Grammar = (fact) => {
  if (fact.states.includes('at_risk')) return atRisk(fact);
  if (fact.states.includes('realized') || fact.states.includes('validated'))
    return valueCapture(fact);
  return null;
};

/**
 * Answer first (narration slice 1), the headline form so no verb must agree with a governed
 * label: "{subject} for {scope} in {period}: {value}." or "… as of {day}: {value}.". Without a
 * bound value it stays the caption "{subject} for {scope}, {period}." (the exhibit draws it).
 */
const figureCaption: Grammar = (fact) => {
  const { value } = fact.figures;
  if (value && fact.labels.template) return voiced(fact, value);
  if (value) {
    const { asOf, period } = fact.labels;
    const when = asOf ? ` as of ${asOf}` : period ? ` in ${period}` : '';
    const lead = fact.labels.attribute ? `${fact.labels.attribute}. ` : '';
    return [text(`${lead}${fact.subject}${scopeClause(fact)}${when}: `), figure(value), text('.')];
  }
  const period = fact.labels.period ? `, ${fact.labels.period}` : '';
  return [text(`${fact.subject}${scopeClause(fact)}${period}.`)];
};

/**
 * Narration slice 1b: a measure voice's sentence, every word slot already resolved from typed
 * parts; the one bound figure goes where {v} was. A directed template shows the figure unsigned
 * (the words carry the sign). A status lead ("X needs a plan amendment.") comes first.
 */
function voiced(fact: AskPlanFact, value: string): readonly AskStorySegment[] | null {
  const [before, after, ...rest] = (fact.labels.template ?? '').split('{v}');
  if (after === undefined || rest.length > 0) return null;
  const bound: AskStorySegment =
    fact.labels.magnitude === 'true' ? { figure: value, magnitude: true } : figure(value);
  const lead = fact.labels.lead ? `${fact.labels.lead} ` : '';
  return [text(`${lead}${before}`), bound, text(after)];
}

/**
 * "{subject}: {achieved} run-rate achieved as of {asOf}, against a {plan} current plan and a
 * {committed} announced commitment." (partner review, value-capture dashboard)
 */
const progress: Grammar = (fact) => {
  const { achieved, plan, committed } = fact.figures;
  if (!achieved || !plan || !committed) return null;
  const asOf = fact.labels.asOf ? ` as of ${fact.labels.asOf},` : '';
  return [
    text(`${fact.subject}: `),
    figure(achieved),
    text(` run-rate achieved${asOf} against a `),
    figure(plan),
    text(' current plan and a '),
    figure(committed),
    text(' announced commitment.'),
  ];
};

/**
 * A ranking's leader and the next member (narration slice 1), their own bound figures only:
 *   top:    "{leader} leads {measure}, at {leader}, followed by {next} at {next}."
 *   bottom: "{leader} is lowest on {measure}, at {leader}, followed by {next} at {next}."
 */
const largest: Grammar = (fact) => {
  const { leader, next } = fact.figures;
  const { leader: first, next: second, end } = fact.labels;
  if (!leader || !next || !first || !second) return null;
  if (fact.labels.rankNoun) {
    // Slice 1b voice: "{#1} is the largest SG&A pool at {v}, ahead of {#2} at {v}."
    const joiner = end === 'bottom' ? ', followed by ' : ', ahead of ';
    return [
      text(`${first} is ${fact.labels.rankNoun} at `),
      figure(leader),
      text(`${joiner}${second} at `),
      figure(next),
      text('.'),
    ];
  }
  const verb = end === 'bottom' ? 'is lowest on' : end === 'top' ? 'leads' : null;
  if (verb === null) return null;
  return [
    text(`${first} ${verb} ${fact.subject}, at `),
    figure(leader),
    text(`, followed by ${second} at `),
    figure(next),
    text('.'),
  ];
};

/**
 * A same-period gap between two sides (HOTFIX 2026-09-27), the bound difference only:
 *   measures: "{measure} exceeds {other measure} by {gap} for {scope}, in {period}."
 *   scopes:   "{measure} for {scope} exceeds {other scope} by {gap} in {period}."
 * When the engine's difference is negative: "{measure} falls short of {other}, a difference of
 * {gap}, …" — the difference is signed and there is no absolute binding, so "falls short … by
 * −$1M" would be a double negative (headed run 2026-09-27 07:28).
 */
const gapToReference: Grammar = (fact) => {
  const { gap } = fact.figures;
  const { reference, scope, period, sides } = fact.labels;
  if (!gap || !reference || !period) return null;
  const below = fact.states.includes('below_reference');
  const relation = below
    ? `falls short of ${reference}, a difference of `
    : `exceeds ${reference} by `;
  const after = below ? ',' : '';
  if (sides === 'measures') {
    const where = scope ? ` for ${scope},` : '';
    return [
      text(`${fact.subject} ${relation}`),
      figure(gap),
      text(`${after}${where} in ${period}.`),
    ];
  }
  if (sides === 'scopes' && scope) {
    return [
      text(`${fact.subject} for ${scope} ${relation}`),
      figure(gap),
      text(`${after} in ${period}.`),
    ];
  }
  return null;
};

const GRAMMARS: Readonly<Partial<Record<AskPlanFact['relation'], Grammar>>> = {
  change,
  largest,
  gap_to_reference: gapToReference,
  progress,
  part_of_whole: partOfWhole,
  figure: figureCaption,
};

/** One reviewed sentence per allowed advisory action (advisor voice; never free text). */
const ACTIONS: Readonly<Record<string, Grammar>> = {
  review_plan_reduction: (fact) =>
    fact.figures.planChange
      ? [
          text('Agree amended targets for the workstreams behind the '),
          figure(fact.figures.planChange),
          text(' change to plan.'),
        ]
      : null,
};

/**
 * One reviewed "why" per allowed advisory action: words only, tied to the same typed state
 * as the action's next step (advisor voice; never free text, never a figure).
 */
const ACTION_WHY: Readonly<Record<string, (fact: AskPlanFact) => string | null>> = {
  review_plan_reduction: (fact) =>
    fact.states.includes('plan_reduced')
      ? 'The plan now sits below the commitment the board approved, so the gap needs an owner and a date.'
      : null,
};

/** The reviewed "why" for a plan's action, or null when no reviewed sentence covers it. */
export function verbalizeActionWhy(
  action: Readonly<{ id: string }> | null,
  fact: AskPlanFact
): string | null {
  return action ? (ACTION_WHY[action.id]?.(fact) ?? null) : null;
}

/** The recommended next step for a plan's action, or null when no reviewed sentence covers it. */
export function verbalizeAction(
  action: Readonly<{ id: string }> | null,
  fact: AskPlanFact
): readonly AskStorySegment[] | null {
  return action ? (ACTIONS[action.id]?.(fact) ?? null) : null;
}

/** The reviewed sentence for one plan fact, or null when no grammar covers it. */
export function verbalizeFact(fact: AskPlanFact): readonly AskStorySegment[] | null {
  return GRAMMARS[fact.relation]?.(fact) ?? null;
}
